import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, MapPin, Crosshair } from "@phosphor-icons/react";
import { MapContainer, TileLayer, Marker, Circle, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// fix default leaflet marker icons under webpack
const redIcon = new L.DivIcon({
  className: "",
  html: '<div style="background:#E50914;width:20px;height:20px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 8px rgba(229,9,20,.6)"></div>',
  iconSize: [20, 20], iconAnchor: [10, 10],
});

const TYPES = [
  ["HEAD_OFFICE", "Head Office"],
  ["BRANCH_OFFICE", "Branch Office"],
  ["COLLECTION_CENTER", "Collection Center"],
  ["WAREHOUSE", "Warehouse"],
  ["PROJECT_SITE", "Project Site"],
  ["CUSTOMER_SITE", "Customer Site"],
  ["HOSPITAL", "Hospital"],
  ["FIELD_REPORTING_POINT", "Field Reporting Point"],
  ["TEMPORARY_SITE", "Temporary Site"],
  ["CUSTOM", "Custom"],
];
const RADII = [50, 100, 250, 500, 1000];

const EMPTY = {
  name: "", code: "", location_type: "HEAD_OFFICE", address: "",
  latitude: 8.524100, longitude: 76.936600, allowed_radius: 100,
  status: "ACTIVE", remarks: "",
};

function ClickToSet({ setLatLng }) {
  useMapEvents({ click(e) { setLatLng(e.latlng.lat, e.latlng.lng); } });
  return null;
}

export default function Locations() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  async function load() {
    try { const { data } = await api.get("/locations"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function openEdit(loc) { setEditing(loc); setForm({ ...EMPTY, ...loc }); setOpen(true); }

  async function save() {
    try {
      if (editing) {
        await api.patch(`/locations/${editing.id}`, form);
        toast.success("Location updated");
      } else {
        await api.post("/locations", form);
        toast.success("Location created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function remove(loc) {
    if (!window.confirm(`Deactivate ${loc.name}?`)) return;
    try { await api.delete(`/locations/${loc.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  function pickCurrentGPS() {
    if (!navigator.geolocation) return toast.error("GPS not available");
    navigator.geolocation.getCurrentPosition(
      (p) => setForm((f) => ({ ...f, latitude: +p.coords.latitude.toFixed(6), longitude: +p.coords.longitude.toFixed(6) })),
      () => toast.error("Could not read GPS"),
      { enableHighAccuracy: true });
  }

  const filtered = rows.filter((r) =>
    !search || r.name?.toLowerCase().includes(search.toLowerCase())
            || r.code?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Geo-Fence</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Office Locations</h1>
          <p className="text-xs text-zinc-500 mt-1">Define approved reporting sites with allowed radius for GPS attendance verification.</p>
        </div>
        <div className="flex gap-3">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
                 className="w-56 bg-[#16161C] border-[#2D2D35] h-10" data-testid="loc-search" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-[#E50914] hover:bg-[#C80812] h-10" data-testid="add-location-button">
                <Plus size={16} className="mr-1" /> Add Location
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white max-w-3xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? `Edit · ${editing.name}` : "New Location"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <Field label="Location Name" value={form.name} onChange={(v)=>setForm({...form, name: v})} testid="loc-name" />
                <Field label="Location Code" value={form.code} onChange={(v)=>setForm({...form, code: v})} testid="loc-code" />
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Type</Label>
                  <Select value={form.location_type} onValueChange={(v)=>setForm({...form, location_type: v})}>
                    <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="loc-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                      {TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Allowed Radius (m)</Label>
                  <Select value={String(form.allowed_radius)} onValueChange={(v)=>setForm({...form, allowed_radius: Number(v)})}>
                    <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="loc-radius"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                      {RADII.map((r) => <SelectItem key={r} value={String(r)}>{r} m</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Latitude" type="number" value={form.latitude} onChange={(v)=>setForm({...form, latitude: Number(v)})} testid="loc-lat" />
                <Field label="Longitude" type="number" value={form.longitude} onChange={(v)=>setForm({...form, longitude: Number(v)})} testid="loc-lng" />
                <div className="col-span-2 flex items-center justify-between">
                  <Button onClick={pickCurrentGPS} variant="outline" className="border-[#2D2D35] bg-transparent" data-testid="loc-pick-current">
                    <Crosshair size={14} className="mr-1" /> Use my current GPS
                  </Button>
                  <span className="text-[11px] text-zinc-500 font-mono">Click on map to set position</span>
                </div>
                <div className="col-span-2 h-72 rounded-md overflow-hidden border border-[#2D2D35]">
                  <MapContainer center={[form.latitude, form.longitude]} zoom={16}
                                style={{ height: "100%", width: "100%" }}>
                    <TileLayer attribution='&copy; OpenStreetMap' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Marker icon={redIcon} position={[form.latitude, form.longitude]} />
                    <Circle center={[form.latitude, form.longitude]} radius={form.allowed_radius}
                            pathOptions={{ color: "#E50914", fillColor: "#E50914", fillOpacity: 0.12, weight: 1.5 }} />
                    <ClickToSet setLatLng={(lat, lng) => setForm((f) => ({ ...f, latitude: +lat.toFixed(6), longitude: +lng.toFixed(6) }))} />
                  </MapContainer>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Address</Label>
                  <Input value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})}
                         className="mt-2 bg-[#0D0D10] border-[#2D2D35]" />
                </div>
                <Field label="Remarks" value={form.remarks} onChange={(v)=>setForm({...form, remarks: v})} />
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Status</Label>
                  <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
                    <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="INACTIVE">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-3">
                <Button variant="outline" onClick={()=>setOpen(false)} className="border-[#2D2D35] bg-transparent">Cancel</Button>
                <Button onClick={save} data-testid="loc-save" className="bg-[#E50914] hover:bg-[#C80812]">
                  {editing ? "Save changes" : "Create"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-[#16161C] border border-[#2D2D35] rounded-md overflow-hidden">
          <table className="w-full" data-testid="locations-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Type</th>
                <th className="text-right p-3">Radius</th>
                <th className="text-left p-3">Coords</th>
                <th className="text-right p-3">Status</th>
                <th className="text-right p-3"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-[#2D2D35]/60 hover:bg-[#1C1C24]">
                  <td className="p-3 font-mono text-xs text-[#E50914]">{r.code}</td>
                  <td className="p-3">{r.name}<div className="text-[10px] text-zinc-500">{r.address}</div></td>
                  <td className="p-3 text-xs"><span className="px-2 py-1 rounded bg-[#0D0D10] border border-[#2D2D35]">{(r.location_type||"").replaceAll("_"," ")}</span></td>
                  <td className="p-3 text-right font-mono text-xs">{r.allowed_radius} m</td>
                  <td className="p-3 font-mono text-[11px] text-zinc-400">{Number(r.latitude).toFixed(4)}, {Number(r.longitude).toFixed(4)}</td>
                  <td className="p-3 text-right">
                    <span className={`text-[10px] px-2 py-1 rounded ${r.status==="ACTIVE" ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-zinc-700/30 text-zinc-400"}`}>{r.status}</span>
                  </td>
                  <td className="p-3 text-right space-x-1">
                    <button onClick={()=>openEdit(r)} className="p-2 hover:text-[#E50914]"><PencilSimple size={16} /></button>
                    <button onClick={()=>remove(r)} className="p-2 hover:text-[#FF3B30]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan="7" className="text-center text-zinc-500 p-8 text-sm">No locations defined yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="bg-[#16161C] border border-[#2D2D35] rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-[#2D2D35] text-sm font-medium tracking-wide flex items-center gap-2">
            <MapPin size={14} className="text-[#E50914]" /> All sites on map
          </div>
          <div className="h-[420px]">
            {rows.length ? (
              <MapContainer center={[rows[0].latitude, rows[0].longitude]} zoom={11} style={{ height: "100%", width: "100%" }}>
                <TileLayer attribution='&copy; OSM' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {rows.map((r) => (
                  <div key={r.id}>
                    <Marker icon={redIcon} position={[r.latitude, r.longitude]}>
                      <Popup>{r.name} ({r.code})<br/>radius {r.allowed_radius}m</Popup>
                    </Marker>
                    <Circle center={[r.latitude, r.longitude]} radius={r.allowed_radius}
                            pathOptions={{ color: r.status==="ACTIVE" ? "#22C55E" : "#71717A",
                                           fillColor: r.status==="ACTIVE" ? "#22C55E" : "#71717A",
                                           fillOpacity: 0.10, weight: 1.5 }} />
                  </div>
                ))}
              </MapContainer>
            ) : <div className="h-full flex items-center justify-center text-xs text-zinc-500">Add a location to see it on map.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type="text", testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input value={value ?? ""} type={type} step="any"
             onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="mt-2 bg-[#0D0D10] border-[#2D2D35] focus:border-[#E50914] focus:ring-1 focus:ring-[#E50914]" />
    </div>
  );
}
